import DynamicString from "@passportinc/hydra-core/components/DynamicString";
import { isExpiredRenewEligible } from "@passportinc/permits-utils/dist/utils/expiredRenewalEligibility";
import cardValidator from "card-validator";
import creditCardType from "credit-card-type";
import _ from "lodash";
import moment from "moment";
import momentTz from "moment-timezone";
import { useEffect } from "react";
import Validator from "validatorjs";
import constants from "../constants";
import { getPermitBarcode, getPermitReceipt } from "../services/permits";

// (x, y) => _.includes(x, y) == _.includes
const basicMatch = _.includes;

const equalityMatch = _.isEqual;

const stringMatch = (haystack, needle) => _.includes(_.toLower(haystack), _.toLower(needle));

const TYPE_MATCH_MAPPING = {
	string: stringMatch,
	boolean: equalityMatch,
	number: equalityMatch,
};

/**
 * This will get a matching function based on the type of input.
 * @param {*} searchTerm The input based on which we get a matching function.
 * @returns {func} searchFunction the matching function. `(haystack, needle) => {}`
 */
export function getMatchingFunction(searchTerm) {
	return _.get(TYPE_MATCH_MAPPING, typeof searchTerm, basicMatch);
}

/**
 * Filter an Array of objects based on the search term.
 * @param {Array<Object>} list The list of objects to filter on.
 * @param {String} searchTerm The search term to use to filter the array.
 * @param {Object} options This is the optional options object.
 * @param {Array<String|fieldObject>} options.fields The list of fields from the object to include
 * in the search. If no list is provided, the first field in the object is used.
 * @example
 * filterObjectList( list, 'charlotte',
 *  {
 *    fields: [
 *      'cityName',
 *      'location.geo.lat',
 *      'location.geo.long',
 *      { name: 'zones', searchFunction: (haystack, needle) => { . . . return Boolean(); }},
 *    ]
 *  }
 * );
 * @example utils/index.test.js : filterObjectList
 *
 * @returns {Array<Object>} The filterd list of objects.
 */
export function filterObjectList(list, searchTerm, options = {}) {
	if (_.isEmpty(list) || searchTerm === undefined || searchTerm === "") return list;

	const search = getMatchingFunction(searchTerm);

	if (_.isEmpty(options)) {
		return _.filter(list, (item) => {
			if (_.isEmpty(item)) return false;
			const field = _.first(_.keys(item));
			const value = _.get(item, field, "");
			return search(value, searchTerm);
		});
	}

	const fields = _.get(options, "fields", []);

	return _.filter(list, (item) => {
		if (_.isEmpty(item)) return false;

		return _.reduce(
			fields,
			(hasMatch, field) => {
				if (hasMatch) return hasMatch;

				if (_.isObject(field)) {
					const customFieldName = _.get(field, "name");
					const customSearchFunction = _.get(field, "searchFunction", "");
					if (!_.isFunction(customSearchFunction) || _.isEmpty(customFieldName)) return false;

					const customField = _.get(item, customFieldName);
					if (_.isEmpty(customField)) return false;

					return Boolean(customSearchFunction(customField, searchTerm));
				}

				const fieldValue = _.get(item, field, "");
				return search(fieldValue, searchTerm);
			},
			false,
		);
	});
}

export const getPaymentCardTypes = (settings) => {
	let cardTypes = _.get(settings, "acceptedcardtypes", "")
		.split(",")
		.filter((word) => /\w/.test(word));
	if (_.includes(cardTypes, "discover") && !_.includes(cardTypes, "jcb")) {
		cardTypes = _.concat(cardTypes, ["jcb"]);
	}
	if (_.includes(cardTypes, "discover") && !_.includes(cardTypes, "unionpay")) {
		cardTypes = _.concat(cardTypes, ["unionpay"]);
	}
	return cardTypes;
};
/**
 * Handles validation for all fields in the form
 * @param {Object} rules - the rules object
 * @param {Array<*>} args - arguments passed from Redux Form Field
 * @Returns {String|null} string if error exists, null otherwise
 */
export const genericValidator =
	(rules) =>
	(...args) => {
		const [value, , , name] = args;

		const rule = { [name]: _.get(rules, name, "") };

		const validation = new Validator(
			{
				[name]: value,
			},
			rule,
		);

		return validation.fails() ? validation.errors.get(name)[0] : null;
	};

/**
 * Validates credit card type based on allowedCardTypes Operator Setting
 * @param {string} cardNumber - The credit card number
 * @param {string} allowedCardTypes - The allowed card types
 * @returns {string} null or error message
 */
export const creditCardTypeValidator = (cardNumber, cardTypes) => {
	const allowedCardTypes = _.get(cardTypes, "allowedCardTypes", cardTypes);
	const cardType = _.replace(_.get(creditCardType(cardNumber), "[0].type"), /[^a-z]/g, "");
	if (_.size(allowedCardTypes) === 0 || _.includes(allowedCardTypes, cardType)) {
		return null;
	}
	return "Invalid card number. See accepted payment types below.";
};

/**
 * add spaces to credit card number
 * @param {string} cardNumber - card number
 * @param {array} gaps - Space patterns in array
 * @returns {string} - card number with spaces
 */
export const addSpacesToCreditCardNumber = (cardNumber, gaps = []) => {
	const cleanedNumber = cardNumber.replace(/\D/g, "");
	const result = [];
	let lastIndex = 0;
	if (!gaps.length) {
		return cleanedNumber;
	}

	gaps.forEach((gap) => {
		result.push(cleanedNumber.substring(lastIndex, gap));
		lastIndex = gap;
	});

	result.push(cleanedNumber.substring(lastIndex));
	return _.filter(result).join(" ");
};

/**
 * Validates credit card number
 * @param {string} cardNumber - The credit card number
 * @returns {string} null or error message
 */
export const creditCardValidator = (cardNumber) => {
	const invalidCardMessage = DynamicString.resolve(
		"hydra_permits_invalid_credit_card_error_message",
		"Invalid Card Number",
	);
	if (cardNumber) {
		const numberValidation = cardValidator.number(cardNumber);
		const creditCardNumber = _.replace(cardNumber, /\s/g, "");
		const cardLength = _.size(creditCardNumber);
		const allowedLengths = _.get(numberValidation, "card.lengths");
		if (_.includes(allowedLengths, cardLength)) {
			return numberValidation.isValid ? null : invalidCardMessage;
		}
		if (cardLength > _.last(allowedLengths)) {
			return invalidCardMessage;
		}
		return " ";
	}
	return null;
};

/**
 * Validates cvv code
 * @param {string} cvv - The cvv number
 * @param {object} formValues card object
 * @returns {string} null or error message
 */
export const cvvCodeValidator = (cvv, formValues) => {
	const cardNumber = _.get(formValues, "card");
	if (cardNumber && cvv) {
		const numberValidation = cardValidator.number(cardNumber);
		const allowedCvvSize = _.get(numberValidation, "card.code.size");
		const codeName = _.get(numberValidation, "card.code.name");
		if (_.size(cvv) !== allowedCvvSize && allowedCvvSize) {
			return `The ${codeName} code must be ${allowedCvvSize} characters.`;
		}
		return null;
	}
	return " ";
};

export const postalCodeValidator = (postalCode) => {
	const postalCodeValidation = cardValidator.postalCode(postalCode);

	const invalidPostalCodeMessage = DynamicString.resolve(
		"hydra_permits_invalid_postal_error_message",
		"Invalid Postal Code",
	);

	return postalCodeValidation.isValid ? null : invalidPostalCodeMessage;
};

export const expirationDateValidator = (date) => {
	const dateValidation = cardValidator.expirationDate(date);

	const invalidCardMessage = DynamicString.resolve(
		"hydra_permits_invalid_credit_card_error_message",
		"Invalid Expiration Date",
	);

	return dateValidation.isValid ? null : invalidCardMessage;
};

export const expirationDateParser = (date) => {
	const dateValidation = cardValidator.expirationDate(date);

	return {
		month: dateValidation.month ? _.padStart(dateValidation.month, 2, "0") : null,
		year: dateValidation.year ? _.padStart(dateValidation.year, 4, "20") : null,
	};
};

/**
 * Custom hook which calls `onChange` fn whenever the defaultValue changes.
 * This should only call `onChange` once during initial render.
 *
 * @param {Function} onChange - onChange handler for an input
 * @param {*} defaultValue - Any default value for an input.
 * @returns {void}
 */
export const useDefaultValue = (onChange, defaultValue) => {
	useEffect(() => {
		onChange(defaultValue);
	}, [onChange, defaultValue]);
};

/**
 * Checks to see if there is a `value` property on the object.
 * If there is, it will use that, otherwise it will default to the
 * already flattend value.
 * This is useful for dropdown values.
 * @example { foo: 'bar', ban: { value: 'baz' } } => { foo: 'bar', ban: 'baz' }
 *
 * @param {Object} values - the values to flatten
 * @returns {Object} Flattened values
 */
export const maybeFlattenValues = (values) => _.mapValues(values, (value) => _.get(value, "value", value));

/**
 * Handles fetching / opening the receipt in a new tab.
 * @param {string} permitId - The ID associated to the permit object
 * @param {string} orderId - The ID associated to the order.
 * @param {Function}
 * onToast - The toast handler. Takes a message and a variant inside of the object.
 * @param {Object} toastVariants - The variants for the toast mechanism. Ex: success, error, info.
 * @returns {void}
 */
export const getViewReceiptHandler = (permitId, orderId, onToast, toastVariants) => async () => {
	onToast({
		message: DynamicString.resolve("hydra_permits_receipt_handler_preparing_message", "Preparing your receipt."),
		variant: toastVariants.info,
	});

	const response = await getPermitReceipt(permitId, orderId);
	const link = _.get(response, "data.link");

	if (link) {
		window.open(link, "__blank");

		onToast({
			message: DynamicString.resolve(
				"hydra_permits_receipt_handler_popup_message",
				"Opening the receipt in a new window. If it does not open, please make sure you have popups enabled.",
			),
			variant: toastVariants.success,
		});
	} else {
		onToast({
			message: DynamicString.resolve(
				"hydra_permits_receipt_handler_error_message",
				"Failed to get a link to the receipt. Please try again.",
			),
			variant: toastVariants.error,
		});
	}
};

/**
 * Handles fetching / opening the barcode pdf in a new tab.
 * @param {string} permitId - The ID associated to the permit object
 * @param {Function}
 * onToast - The toast handler. Takes a message and a variant inside of the object.
 * @param {Object} toastVariants - The variants for the toast mechanism. Ex: success, error, info.
 * @returns {void}
 */
export const getBarcodeHandler = (permitId, onToast, toastVariants) => async () => {
	onToast({
		message: DynamicString.resolve("hydra_permits_barcode_handler_preparing_message", "Preparing your barcode."),
		variant: toastVariants.info,
	});

	const response = await getPermitBarcode(permitId);
	const link = _.get(response, "data.link");

	if (link) {
		window.open(link, "__blank");

		onToast({
			message: DynamicString.resolve(
				"hydra_permits_receipt_barcode_popup_message",
				"Opening the barcode in a new window. If it does not open, please make sure you have popups enabled.",
			),
			variant: toastVariants.success,
		});
	} else {
		onToast({
			message: DynamicString.resolve(
				"hydra_permits_barcode_handler_error_message",
				"Failed to get a link to the barcode. Please try again.",
			),
			variant: toastVariants.error,
		});
	}
};

/**
 * Attempts to return the invocation of the given method.
 * If the method fails, `fallback` is returned.
 * @param {function} method The method to invoke.
 * @param {any} fallback The fallback value if method throws.
 * @param {...any} args Arguments to pass to `method`.
 * @returns {any} Either the results of `method` or the fallback value.
 */
export const attempt = (method, fallback, ...args) => {
	try {
		return method(...args);
	} catch (_e) {
		return fallback;
	}
};

export const tryJsonParse = (value) => attempt(JSON.parse, value, value);

/**
 * Reads a boolean-ish operator setting. Settings arrive as strings and are commonly blank,
 * so parse defensively - `JSON.parse("")` throws, and doing that in render takes the page down.
 * Matches the enabled values rather than coercing: `tryJsonParse` returns the raw string when
 * parsing fails, and coercing that would make "off" and "False" both read as enabled.
 * @param {Object} settings - Operator settings.
 * @param {string} key - Setting key to read.
 * @returns {boolean} Whether the setting is enabled.
 */
export const isSettingEnabled = (settings, key) => {
	const parsed = tryJsonParse(settings?.[key] ?? false);
	return parsed === true || parsed === 1;
};

/**
 * Normalizes link code
 * @param {string} linkCode - user inputted
 * @return {string} formatted link code
 */
export const normalizeLinkCode = (linkCode) => {
	const formatted = _.toUpper(linkCode).replace(/[^A-Z0-9]/g, "");
	if (formatted.length === 6) {
		return formatted;
	}
	return formatted.match(/.{1,4}/g).join("-");
};

export const stringToAscii = (string) => _.map(string, (s) => s.charCodeAt(0)).join("");

export const permitToDecimalNumber = (permit, options = {}) => {
	const { prefix = "", max_length: _maxLength = 100 } = options;

	const maxLength = _.isNumber(_maxLength) && _maxLength > 0 ? parseInt(_maxLength, 10) : 100;

	const sqrt = Math.sqrt(_.parseInt(stringToAscii(permit.number)));
	const parts = sqrt.toString().split(".");
	const newNumber = _.parseInt(parts[1]) + _.parseInt(parts[0]);
	const randomString = newNumber.toString().substr(-maxLength);
	const milliString = moment.utc(permit.created_at).valueOf().toString().substr(-3);

	const baseInt = _.parseInt(randomString) + _.parseInt(milliString);

	if (prefix) {
		return [prefix, baseInt].join("");
	}

	return baseInt;
};

export const getTibaBarcodeNumber = (permit) => {
	const prefix = _.get(permit, "permission.gates[0].config.facility_number").toString();
	const maxLength = _.get(permit, "permission.gates[0].config.badge_length", 0);
	const numberLength = parseInt(maxLength, 10) - prefix.length;

	return permitToDecimalNumber(permit, { prefix, max_length: numberLength }).toString();
};

export const gatedBarcodeHandlers = {
	tiba: getTibaBarcodeNumber,
};

/**
 * Calculates guest pass counts for a permit.
 * - Active: passes with redemptions whose end_date is in the future.
 * - Expired: passes with redemptions whose end_date is in the past,
 *   plus all unredeemed passes if permit.end_date is in the past.
 * - Available: passes not active or expired.
 * @param {Object} permit - The permit object.
 * @returns {Object} { active, expired, available, max }
 */
export const getGuestPassCounts = (permit) => {
	const passTotal = _.get(permit, "visitor.quantity", 0);
	const redemptions = _.get(permit, "visitor.redemptions", []);
	const permitEndDate = _.get(permit, "end_date");
	const now = moment();

	// A redemption written before scheduling existed (PER-4318) has no issue_date.
	const hasStarted = (r) => !r.issue_date || moment(r.issue_date).isSameOrBefore(now);

	let issued = 0;
	let active = 0;
	let expiredRedemptions = 0;

	for (const redemption of redemptions) {
		if (moment(redemption.end_date).isBefore(now)) expiredRedemptions += 1;
		else if (hasStarted(redemption)) active += 1;
		else issued += 1;
	}

	// Unredeemed passes
	const unredeemed = passTotal - redemptions.length;

	// If permit is expired, all unredeemed passes are expired too
	let expired = expiredRedemptions;
	if (permitEndDate && moment(permitEndDate).isBefore(now)) {
		expired += unredeemed;
	}

	// Available is whatever is left.
	const available = passTotal - active - expired - issued;
	const max = _.get(permit, "type.settings.visitor.max_active", 0);

	return {
		issued,
		active,
		expired,
		available,
		max,
	};
};

/**
 * Gets the dropdown values for quantity picker.
 * @param {Object} settings - The permit type settings.
 * @param {Number} current - the current number of passes (if any)
 * @returns {Array<{label, value}>} - Array of dropdown values for quantity picker.
 */
export const getQuantityDropdownValues = ({ settings } = {}, current = 0) => {
	const interval = _.get(settings, "visitor.interval", 1);
	const min = _.get(settings, "visitor.min", interval);
	const max = _.get(settings, "visitor.max", 25);

	if (current > max) return [];

	const options = [];
	const allotment = max - current;
	let i = min - interval;

	while (i < allotment) {
		if (i + interval > allotment) {
			const inc = allotment - i;

			i += inc;
		} else {
			i += interval;
		}

		options.push({ value: i, label: i });
	}

	return options;
};

/**
 * Whether the holder can buy more visitor passes against type.settings.visitor.max.
 * Mirrors AdditionalPassesModalContainer so Manage Passes can hide a dead-end CTA
 * when the pass pool is already full (PER-4170).
 * @param {Object} permit
 * @returns {boolean}
 */
export const canPurchaseAdditionalPasses = (permit) => {
	const maxPurchase = _.get(permit, "type.settings.visitor.max", 0);
	const totalPasses = _.get(permit, "visitor.quantity", 0);

	if (maxPurchase && totalPasses >= maxPurchase) {
		return false;
	}

	return true;
};

export const isInValidTime = (permit, timezone) => {
	const validTimes = _.get(permit, "permission.time_limit.valid", []);
	if (!validTimes.length) return true;

	const validDays = _.filter(validTimes, (time) => _.includes(time.recurring_days_of_week, moment().day()));

	if (!validDays.length) return false;

	const inValidHours = _.map(validDays, (time) => {
		const today = momentTz.tz(new Date(), timezone).format();
		const currentTime = moment(today).year(2020).month(0).date(1).format();
		const startTime = moment(time.start_time).year(2020).month(0).format();
		const endTime = moment(time.end_time).year(2020).month(0).format();

		return moment(currentTime).isBetween(startTime, endTime);
	});

	return _.includes(inValidHours, true);
};

export const getCardValuesFromForm = (form) => {
	const { month, year } = expirationDateParser(form.expiration);
	const number = _.get(form, "card", "");
	const postalCode = _.get(form, "postal_code", "");
	const cvv = _.get(form, "cvv", "");

	return {
		card_number: number,
		exp_year: year,
		exp_month: month,
		postal_code: postalCode,
		cvv,
	};
};

export const canRenew = (permit) => {
	if (_.has(permit, "can_renew")) return _.get(permit, "can_renew");
	const futureCycleDates = _.filter(_.get(permit, "cycle.dates", []), (date) =>
		moment(_.get(date, "end_date")).isAfter(moment(_.get(permit, "end_date"))),
	);
	const reachedRenewalLimit =
		_.has(permit, "type.settings.max_renewal_limit") &&
		_.get(permit, "renewed_times", 0) >= _.get(permit, "type.settings.max_renewal_limit");
	const status = _.get(permit, "status");
	return (
		(status === constants.PERMIT_STATUSES.ACTIVE ||
			(status === constants.PERMIT_STATUSES.WAITLISTED &&
				_.get(permit, "type.settings.waitlist_renewal.charge", false)) ||
			(status === constants.PERMIT_STATUSES.EXPIRED && isExpiredRenewEligible(permit, _.get(permit, "type")))) &&
		!_.isEmpty(futureCycleDates) &&
		_.get(permit, "type.settings.allow_extensions", true) &&
		!reachedRenewalLimit
	);
};

export const isWithinRenewalRange = (permit) => {
	const currentTime = moment();
	const permitCycle = _.get(permit, "cycle");
	const permitCanRenew = canRenew(permit);

	const renewalWindow = permitCycle ? _.pick(permitCycle, ["future_renew_days_min", "future_renew_days_max"]) : null;
	const hasRenewalWindow = renewalWindow && !_.isEmpty(renewalWindow);

	const renewTimes = !hasRenewalWindow
		? null
		: {
				start: moment(permit.end_date).startOf("hour").subtract(renewalWindow.future_renew_days_max, "days"),
				end: moment(permit.end_date).startOf("hour").subtract(renewalWindow.future_renew_days_min, "days"),
			};

	return (
		renewTimes &&
		permitCanRenew &&
		currentTime.isSameOrAfter(renewTimes.start.local()) &&
		currentTime.isBefore(renewTimes.end.local())
	);
};

export const getRenewalDate = (permit) => {
	const permitCycle = _.get(permit, "cycle");
	const renewalWindow = permitCycle ? _.pick(permitCycle, ["future_renew_days_min", "future_renew_days_max"]) : null;
	return moment(permit.end_date).startOf("hour").subtract(renewalWindow.future_renew_days_min, "days").format();
};

/**
 * Returns status message and optional type for permit/application (e.g. waitlist position, expiry, documents required).
 * Used when permits come from bulk search (with optional hydrate_waitlist for waitlist_position) or single permit.
 * @param {Object} permit - Permit or application object (status, end_date, waitlist_id, type.settings, cycle.timezone, can_renew)
 * @param {number|null} [waitlistPosition=null] - One-based waitlist position (e.g. from bulk search hydrate_waitlist)
 * @param {boolean} [isPermit=true] - Whether the item is a permit (vs application)
 * @returns {string}
 */
export function getPermitStatusMessage(permit) {
	const endDate = _.get(permit, "end_date");
	const status = _.get(permit, "status");
	const waitlistId = _.get(permit, "waitlist_id");
	const waitlistPosition = _.get(permit, "waitlist_position");
	const permitCanRenew = canRenew(permit) && waitlistId;
	const showPosition =
		status === "waitlisted" && waitlistPosition && _.get(permit, "type.settings.waitlist_renewal.show_position", false);
	const offeredHasDocuments = status === "offered" && _.get(permit, "type.documents", []).length > 0;

	if (permitCanRenew && endDate && momentTz(endDate).isAfter()) {
		return `Your spot in line will expire ${momentTz(endDate).fromNow()}.`;
	}

	if (showPosition && waitlistPosition >= 0) {
		return `You are ${ordinalSuffix(waitlistPosition + 1)} in line on the waitlist.`;
	}

	if (offeredHasDocuments) {
		return "You must upload the required documentation in order to claim your permit.";
	}

	let expiresMessage = "";
	if (endDate && momentTz(endDate).isAfter()) {
		const timezone = _.get(permit, "cycle.timezone", "local");
		expiresMessage = `Expires ${momentTz(endDate).tz(timezone).format("MMM DD, YYYY")}`;
	}

	return expiresMessage;
}

/** 1 → "1st", 2 → "2nd", 3 → "3rd", etc. */
function ordinalSuffix(i) {
	const j = i % 10;
	const k = i % 100;
	if (j === 1 && k !== 11) return `${i}st`;
	if (j === 2 && k !== 12) return `${i}nd`;
	if (j === 3 && k !== 13) return `${i}rd`;
	return `${i}th`;
}

export const formatOptionsForDropdown = (labelKey, valueKey) => (data) =>
	_.map(data, (option) => ({
		label: option[labelKey],
		value: option[valueKey],
	}));

/**
 * Strips spaces and hyphens from a plate as it is entered.
 *
 * Permit applications reject both characters, so a plate stored as `ABC123` would
 * never match a lookup typed or pasted as `AB-C 123` — the check would report the
 * vehicle as unpermitted when it is not.
 *
 * @param {string} value - Raw plate input
 * @returns {string} The plate with spaces and hyphens removed
 */
export const normalizePlateInput = (value) => _.replace(_.toString(value), /[- ]/g, "");

/**
 * Formats a currency number by the locale string
 * @param {string} localStr - the locale number string
 * @returns {string} - converted number to locale
 */
// biome-ignore lint/suspicious/noShadowRestrictedNames: intentional wrapper around Number.prototype.toLocaleString
export const toLocaleString =
	(localStr = "es-US") =>
	(num = 0) =>
		num.toFixed(2).toLocaleString(localStr);

/**
 * Merges 'custom' field array with other fields to create one single field object
 * @param {Object} current - the current field array to be merged with custom fields
 * @param {array<Object>} customFields - the array of custom fields
 * @returns {Object} - object containing fields
 */
export const mergeCustomFields = (current, customFields) =>
	_.reduce(
		customFields,
		(acc, curr) => ({
			...acc,
			[curr.key]: {
				...curr,
			},
		}),
		_.omit(current, "custom"),
	);

/**
 * Sets a new window location path if there is no operator found or if
 * a permitsSubdomain (CMR Pay URL) exists.
 * @property {object} domainInfo data - the current field array to be merged with custom fields
 * @property {string} domainInfo.subdomain - an operator subdomain corresponding to an RMC operator
 * @property {string} domainInfo.permitsSubdomain - an operator subdomain corresponding to a CMR
 * operator if it is different than the RMC operator
 * @property {string} currentSubdomain - the actual subdomain entered in the URL
 * @returns {void}
 */
export const maybeRedirect = ({ subdomain, permitsSubdomain, currentSubdomain }) => {
	if (!subdomain) {
		window.location.replace(constants.PASSPORT_HOME_PAGE);
	}

	if (permitsSubdomain && permitsSubdomain !== currentSubdomain) {
		const newLocation = window.location.href.replace(subdomain, permitsSubdomain);
		window.location.replace(newLocation);
	}
};

/** Operator zone used when the operator has none configured. */
export const OPERATOR_TIMEZONE_FALLBACK = "US/Eastern";

/**
 * The single source for the zone every guest-pass date is rendered in.
 * @param {Object} user - The hydra-core user, as supplied by withUser.
 * @returns {string} The operator's IANA timezone, or the fallback.
 */
export const getOperatorTimezone = (user) => _.get(user, "operator.timezone") || OPERATOR_TIMEZONE_FALLBACK;

/**
 * @param {string} iso - ISO-8601 instant, as stored by permits-core.
 * @param {string} timezone - The operator's IANA timezone.
 * @param {string} format - A moment format string.
 * @returns {string} The instant rendered in the operator's zone, or "" when there is no instant.
 */
export const formatInOperatorZone = (iso, timezone, format) =>
	iso ? momentTz.tz(iso, timezone || OPERATOR_TIMEZONE_FALLBACK).format(format) : "";

/**
 * Whether a permit type may still be purchased from the customer portal.
 *
 * `types` is the already-BFF-filtered list from `usePermitTypes`, so absence from it
 * is itself the signal. Fails open on an empty list: a failed types fetch must not
 * strand a customer mid-flow or empty their cart.
 * @param {string} typeId - The permit type id to test.
 * @param {Array<Object>} types - Permit types from `usePermitTypes`.
 * @returns {boolean} True when the type may be purchased.
 */
export const isPermitTypeVisible = (typeId, types) => {
	if (!types?.length) return true;

	const type = types.find((t) => t.id === typeId);
	const { ALL, CUSTOMER } = constants.PERMIT_TYPE_VISIBILITIES;

	// Mirrors hydra-api's own filter (eligible.js `isVisible`), which passes a type with no
	// visibility set - treating one as back-office here would block a type the server calls public.
	return !!type && (!type.visibility || [ALL, CUSTOMER].includes(type.visibility));
};
